import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem('utmost_language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('utmost_language', language);
    document.documentElement.lang = language;
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'fr' : 'en');
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return key; // Return key if translation not found
      }
    }
    
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Translations object
const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      insurance: 'Insurance',
      investment: 'Investment',
      mortgage: 'Mortgage',
      team: 'Our Team',
      partners: 'Partners',
      resources: 'Resources',
      contact: 'Contact',
      blog: 'Blog',
      creditCounselling: 'Debt Management',
      getQuote: 'Get a Quote',
      bookConsultation: 'Book Consultation'
    },
    // Hero Section
    hero: {
      badge: 'Your Trusted Financial Partner',
      title: 'Build Your Financial Future',
      titleHighlight: 'With Confidence',
      subtitle: 'Utmost Financial Inc. helps Canadians protect what matters most, reduce financial stress, and build long-term wealth through insurance, investments, and mortgage solutions.',
      bookConsultation: 'Book Free Consultation',
      getQuote: 'Get a Quote'
    },
    // Services Section
    services: {
      label: 'What We Offer',
      title: 'Our Core Services',
      subtitle: 'Comprehensive financial solutions tailored to your unique situation and goals',
      insurance: {
        title: 'Insurance',
        description: 'Life, critical illness, disability, and travel insurance to protect you and your family.',
        lifeInsurance: 'Life Insurance',
        criticalIllness: 'Critical Illness',
        visitorsCanada: 'Visitors to Canada',
        explore: 'Explore Insurance'
      },
      investment: {
        title: 'Investments',
        description: 'Build wealth through strategic planning and tax-advantaged investment accounts.',
        rrspTfsa: 'RRSP & TFSA',
        respFhsa: 'RESP & FHSA',
        segregatedFunds: 'Segregated Funds',
        explore: 'Explore Investments'
      },
      mortgage: {
        title: 'Mortgage',
        description: 'Find the perfect mortgage solution through DLC Utmost Financial.',
        purchaseRefinance: 'Purchase & Refinance',
        newToCanada: 'New to Canada',
        selfEmployed: 'Self-Employed',
        explore: 'Explore Mortgage'
      }
    },
    // Why Choose Us Section
    whyChooseUs: {
      label: 'Why Utmost Financial',
      title: 'The Utmost Difference',
      subtitle: 'Experience the difference of working with dedicated financial professionals',
      stats: {
        advisors: 'Expert Advisors',
        advisorsDesc: 'Licensed professionals ready to help',
        provinces: 'Provinces Served',
        provincesDesc: 'MB, SK, AB & BC licensed',
        lenders: 'Lender Partners',
        lendersDesc: 'Access to competitive rates',
        carriers: 'Insurance Carriers',
        carriersDesc: "Canada's top providers"
      },
      coreValues: {
        title: 'Our Core Values',
        clientFirst: {
          title: 'Client-First Approach',
          description: 'Your goals and needs always come first. We listen, understand, and tailor solutions specifically for you.'
        },
        transparent: {
          title: 'Transparent Advice',
          description: 'No hidden fees, no confusing jargon. We explain every option clearly so you can make informed decisions.'
        },
        longTerm: {
          title: 'Long-Term Relationships',
          description: "We're not just here for one transaction. We build lasting partnerships to support your financial journey."
        }
      }
    },
    // Who We Serve Section
    whoWeServe: {
      label: 'Who We Help',
      title: 'Solutions for Every Stage of Life',
      subtitle: 'We serve Canadians at every financial milestone',
      firstTimeBuyers: {
        title: 'First-Time Home Buyers',
        description: 'Navigate your first purchase with expert guidance on mortgages, down payments, and FHSA.'
      },
      families: {
        title: 'Growing Families',
        description: 'Protect your loved ones with life insurance and start saving for education with RESPs.'
      },
      businessOwners: {
        title: 'Business Owners',
        description: 'Group benefits, key person insurance, and corporate investment strategies.'
      },
      newToCanada: {
        title: 'New to Canada',
        description: 'Specialized mortgage programs and visitor insurance for newcomers and immigrants.'
      },
      preRetirees: {
        title: 'Pre-Retirees',
        description: 'Maximize your retirement savings with RRSPs, pension transfers, and income planning.'
      },
      retirees: {
        title: 'Retirees',
        description: 'CHIP reverse mortgages, annuities, and estate planning for your golden years.'
      }
    },
    // Process Section
    process: {
      label: 'How It Works',
      title: 'Our Simple Process',
      subtitle: "Getting started is easy - here's how we work with you",
      step1: {
        title: 'Free Consultation',
        description: 'Schedule a no-obligation call to discuss your financial goals and needs.'
      },
      step2: {
        title: 'Personalized Plan',
        description: 'We analyze your situation and create a customized financial strategy.'
      },
      step3: {
        title: 'Implementation',
        description: 'We help you execute the plan with the right products and solutions.'
      },
      step4: {
        title: 'Ongoing Support',
        description: 'Regular reviews and adjustments to keep your plan on track.'
      }
    },
    // Resources Section
    resources: {
      label: 'Resources',
      title: 'Helpful Tools & Resources',
      quoteCalculator: {
        title: 'Insurance Quote Calculator',
        description: 'Get instant estimates for life and critical illness insurance.',
        cta: 'Get a Quote'
      },
      financialResources: {
        title: 'Financial Resources',
        description: 'Access calculators, guides, and educational content.',
        cta: 'Browse Resources'
      },
      blog: {
        title: 'Blog & Insights',
        description: 'Read our latest articles on insurance, mortgages, and investments.',
        cta: 'Read Blog'
      }
    },
    // CTA Section
    cta: {
      title: 'Ready to Take the Next Step?',
      subtitle: 'Book a free consultation and get a clear path to your financial goals. No pressure, no obligation.',
      bookConsultation: 'Book Free Consultation',
      meetTeam: 'Meet Our Team'
    },
    // Footer
    footer: {
      companyDescription: 'Your trusted partner for insurance, investment, and mortgage solutions in Canada.',
      services: 'Services',
      company: 'Company',
      aboutUs: 'About Us',
      ourTeam: 'Our Team',
      partners: 'Partners',
      resources: 'Resources',
      contactUs: 'Contact Us',
      mortgageServices: 'Mortgage Services',
      mortgagesOfferedThrough: 'Mortgages offered through:',
      visitDlc: 'Visit DLC Website',
      newsletter: {
        title: 'Stay Informed',
        subtitle: 'Get the latest financial tips and updates delivered to your inbox.',
        placeholder: 'Enter your email',
        subscribe: 'Subscribe',
        subscribing: 'Subscribing...',
        success: 'Thank you for subscribing!',
        error: 'Please enter a valid email address.'
      },
      copyright: '© {year} Utmost Financial Inc. All rights reserved.'
    },
    // Common
    common: {
      learnMore: 'Learn More',
      readMore: 'Read More',
      viewAll: 'View All',
      submit: 'Submit',
      cancel: 'Cancel',
      loading: 'Loading...',
      error: 'An error occurred',
      success: 'Success',
      phone: 'Phone',
      email: 'Email',
      address: 'Address'
    },
    // Contact Page
    contact: {
      title: 'Contact Us',
      subtitle: "Have questions? We'd love to hear from you.",
      form: {
        name: 'Full Name',
        email: 'Email Address',
        phone: 'Phone Number',
        subject: 'Subject',
        message: 'Message',
        submit: 'Send Message',
        sending: 'Sending...',
        success: 'Your message has been sent successfully!',
        error: 'There was an error sending your message. Please try again.'
      },
      offices: {
        title: 'Our Offices',
        winnipeg: 'Winnipeg Office',
        edmonton: 'Edmonton Office'
      }
    },
    // Blog Page
    blog: {
      title: 'Financial Insights Blog',
      subtitle: 'Tips, guides, and news to help you make informed financial decisions',
      search: 'Search articles...',
      categories: {
        all: 'All Posts',
        insurance: 'Insurance',
        mortgage: 'Mortgage',
        investment: 'Investment'
      },
      noResults: 'No articles found',
      readMore: 'Read More',
      backToBlog: 'Back to Blog',
      shareArticle: 'Share this article:',
      needAdvice: 'Need Expert Advice?',
      needAdviceDesc: 'Our team of licensed professionals is here to help you make informed financial decisions.',
      speakAdvisor: 'Speak with an Advisor',
      relatedArticles: 'Related Articles'
    },
    // Insurance Page
    insurance: {
      title: 'Insurance Solutions',
      subtitle: "Comprehensive protection for you and your loved ones through Canada's leading insurance carriers",
      getQuote: 'Get a Free Quote',
      sections: {
        lifeHealth: {
          title: 'Life & Health Protection',
          subtitle: "Essential coverage to protect you and your family's financial security"
        },
        travel: {
          title: 'Travel & Visitor Insurance',
          subtitle: 'Protection for travelers and visitors to Canada'
        }
      },
      quoteEstimator: {
        title: 'Insurance Quote Estimator',
        subtitle: 'Get an instant estimate for your insurance coverage',
        disclaimer: 'This calculator provides estimates only based on industry averages. Actual premiums may vary based on health history and insurer underwriting. Contact our advisors for accurate quotes.',
        calculate: 'Calculate My Estimate',
        readyForQuote: 'Ready for an Accurate Quote?',
        readyForQuoteDesc: 'Contact our licensed advisors for personalized quotes with the best rates from multiple insurers.',
        speakAdvisor: 'Speak with an Advisor'
      }
    },
    // Team Page
    team: {
      title: 'Meet Our Team',
      subtitle: 'Experienced professionals dedicated to your financial success',
      managingPartners: 'Managing Partners',
      mortgageTeam: 'Mortgage Team',
      insuranceTeam: 'Insurance & Investments Team',
      contactInfo: 'Contact Information'
    },
    // Testimonials
    testimonials: {
      title: 'What Our Clients Say',
      subtitle: 'Real stories from real clients across Canada'
    }
  },
  fr: {
    // Navigation
    nav: {
      home: 'Accueil',
      insurance: 'Assurance',
      investment: 'Investissement',
      mortgage: 'Hypothèque',
      team: 'Notre Équipe',
      partners: 'Partenaires',
      resources: 'Ressources',
      contact: 'Contact',
      blog: 'Blogue',
      creditCounselling: 'Gestion de Dettes',
      getQuote: 'Obtenir une Soumission',
      bookConsultation: 'Prendre Rendez-vous'
    },
    // Hero Section
    hero: {
      badge: 'Votre Partenaire Financier de Confiance',
      title: 'Construisez Votre Avenir Financier',
      titleHighlight: 'Avec Confiance',
      subtitle: 'Utmost Financial Inc. aide les Canadiens à protéger ce qui compte le plus, à réduire le stress financier et à bâtir une richesse à long terme grâce à l\'assurance, aux investissements et aux solutions hypothécaires.',
      bookConsultation: 'Consultation Gratuite',
      getQuote: 'Obtenir une Soumission'
    },
    // Services Section
    services: {
      label: 'Ce Que Nous Offrons',
      title: 'Nos Services Principaux',
      subtitle: 'Des solutions financières complètes adaptées à votre situation et vos objectifs uniques',
      insurance: {
        title: 'Assurance',
        description: 'Assurance vie, maladies graves, invalidité et voyage pour vous protéger, vous et votre famille.',
        lifeInsurance: 'Assurance Vie',
        criticalIllness: 'Maladies Graves',
        visitorsCanada: 'Visiteurs au Canada',
        explore: 'Explorer l\'Assurance'
      },
      investment: {
        title: 'Investissements',
        description: 'Bâtissez votre patrimoine grâce à une planification stratégique et des comptes d\'investissement avantageux fiscalement.',
        rrspTfsa: 'REER & CELI',
        respFhsa: 'REEE & CELIAPP',
        segregatedFunds: 'Fonds Distincts',
        explore: 'Explorer les Investissements'
      },
      mortgage: {
        title: 'Hypothèque',
        description: 'Trouvez la solution hypothécaire parfaite grâce à DLC Utmost Financial.',
        purchaseRefinance: 'Achat & Refinancement',
        newToCanada: 'Nouveaux Arrivants',
        selfEmployed: 'Travailleurs Autonomes',
        explore: 'Explorer l\'Hypothèque'
      }
    },
    // Why Choose Us Section
    whyChooseUs: {
      label: 'Pourquoi Utmost Financial',
      title: 'La Différence Utmost',
      subtitle: 'Découvrez la différence de travailler avec des professionnels financiers dévoués',
      stats: {
        advisors: 'Conseillers Experts',
        advisorsDesc: 'Professionnels licenciés prêts à vous aider',
        provinces: 'Provinces Desservies',
        provincesDesc: 'Licenciés au MB, SK, AB & BC',
        lenders: 'Partenaires Prêteurs',
        lendersDesc: 'Accès à des taux compétitifs',
        carriers: 'Assureurs',
        carriersDesc: 'Les meilleurs fournisseurs du Canada'
      },
      coreValues: {
        title: 'Nos Valeurs Fondamentales',
        clientFirst: {
          title: 'Approche Centrée sur le Client',
          description: 'Vos objectifs et besoins passent toujours en premier. Nous écoutons, comprenons et adaptons les solutions spécifiquement pour vous.'
        },
        transparent: {
          title: 'Conseils Transparents',
          description: 'Pas de frais cachés, pas de jargon confus. Nous expliquons chaque option clairement pour que vous puissiez prendre des décisions éclairées.'
        },
        longTerm: {
          title: 'Relations à Long Terme',
          description: 'Nous ne sommes pas là pour une seule transaction. Nous bâtissons des partenariats durables pour soutenir votre parcours financier.'
        }
      }
    },
    // Who We Serve Section
    whoWeServe: {
      label: 'Qui Nous Aidons',
      title: 'Solutions pour Chaque Étape de la Vie',
      subtitle: 'Nous servons les Canadiens à chaque étape financière importante',
      firstTimeBuyers: {
        title: 'Premiers Acheteurs',
        description: 'Naviguez votre premier achat avec des conseils d\'experts sur les hypothèques, les mises de fonds et le CELIAPP.'
      },
      families: {
        title: 'Familles en Croissance',
        description: 'Protégez vos proches avec une assurance vie et commencez à épargner pour l\'éducation avec les REEE.'
      },
      businessOwners: {
        title: 'Propriétaires d\'Entreprise',
        description: 'Avantages collectifs, assurance personne clé et stratégies d\'investissement corporatif.'
      },
      newToCanada: {
        title: 'Nouveaux au Canada',
        description: 'Programmes hypothécaires spécialisés et assurance visiteurs pour les nouveaux arrivants et immigrants.'
      },
      preRetirees: {
        title: 'Pré-Retraités',
        description: 'Maximisez votre épargne-retraite avec les REER, transferts de pension et planification du revenu.'
      },
      retirees: {
        title: 'Retraités',
        description: 'Hypothèques inversées CHIP, rentes et planification successorale pour vos années dorées.'
      }
    },
    // Process Section
    process: {
      label: 'Comment Ça Fonctionne',
      title: 'Notre Processus Simple',
      subtitle: 'Commencer est facile - voici comment nous travaillons avec vous',
      step1: {
        title: 'Consultation Gratuite',
        description: 'Planifiez un appel sans engagement pour discuter de vos objectifs et besoins financiers.'
      },
      step2: {
        title: 'Plan Personnalisé',
        description: 'Nous analysons votre situation et créons une stratégie financière personnalisée.'
      },
      step3: {
        title: 'Mise en Œuvre',
        description: 'Nous vous aidons à exécuter le plan avec les bons produits et solutions.'
      },
      step4: {
        title: 'Soutien Continu',
        description: 'Révisions régulières et ajustements pour garder votre plan sur la bonne voie.'
      }
    },
    // Resources Section
    resources: {
      label: 'Ressources',
      title: 'Outils et Ressources Utiles',
      quoteCalculator: {
        title: 'Calculateur de Soumission',
        description: 'Obtenez des estimations instantanées pour l\'assurance vie et maladies graves.',
        cta: 'Obtenir une Soumission'
      },
      financialResources: {
        title: 'Ressources Financières',
        description: 'Accédez à des calculateurs, guides et contenu éducatif.',
        cta: 'Parcourir les Ressources'
      },
      blog: {
        title: 'Blogue & Perspectives',
        description: 'Lisez nos derniers articles sur l\'assurance, les hypothèques et les investissements.',
        cta: 'Lire le Blogue'
      }
    },
    // CTA Section
    cta: {
      title: 'Prêt à Passer à l\'Étape Suivante?',
      subtitle: 'Réservez une consultation gratuite et obtenez un chemin clair vers vos objectifs financiers. Sans pression, sans obligation.',
      bookConsultation: 'Consultation Gratuite',
      meetTeam: 'Rencontrer Notre Équipe'
    },
    // Footer
    footer: {
      companyDescription: 'Votre partenaire de confiance pour les solutions d\'assurance, d\'investissement et hypothécaires au Canada.',
      services: 'Services',
      company: 'Entreprise',
      aboutUs: 'À Propos',
      ourTeam: 'Notre Équipe',
      partners: 'Partenaires',
      resources: 'Ressources',
      contactUs: 'Nous Contacter',
      mortgageServices: 'Services Hypothécaires',
      mortgagesOfferedThrough: 'Hypothèques offertes par:',
      visitDlc: 'Visiter le Site DLC',
      newsletter: {
        title: 'Restez Informé',
        subtitle: 'Recevez les derniers conseils financiers et mises à jour dans votre boîte de réception.',
        placeholder: 'Entrez votre courriel',
        subscribe: 'S\'abonner',
        subscribing: 'Abonnement...',
        success: 'Merci de vous être abonné!',
        error: 'Veuillez entrer une adresse courriel valide.'
      },
      copyright: '© {year} Utmost Financial Inc. Tous droits réservés.'
    },
    // Common
    common: {
      learnMore: 'En Savoir Plus',
      readMore: 'Lire la Suite',
      viewAll: 'Voir Tout',
      submit: 'Soumettre',
      cancel: 'Annuler',
      loading: 'Chargement...',
      error: 'Une erreur s\'est produite',
      success: 'Succès',
      phone: 'Téléphone',
      email: 'Courriel',
      address: 'Adresse'
    },
    // Contact Page
    contact: {
      title: 'Nous Contacter',
      subtitle: 'Vous avez des questions? Nous serions ravis de vous entendre.',
      form: {
        name: 'Nom Complet',
        email: 'Adresse Courriel',
        phone: 'Numéro de Téléphone',
        subject: 'Sujet',
        message: 'Message',
        submit: 'Envoyer le Message',
        sending: 'Envoi en cours...',
        success: 'Votre message a été envoyé avec succès!',
        error: 'Une erreur s\'est produite lors de l\'envoi de votre message. Veuillez réessayer.'
      },
      offices: {
        title: 'Nos Bureaux',
        winnipeg: 'Bureau de Winnipeg',
        edmonton: 'Bureau d\'Edmonton'
      }
    },
    // Blog Page
    blog: {
      title: 'Blogue de Perspectives Financières',
      subtitle: 'Conseils, guides et nouvelles pour vous aider à prendre des décisions financières éclairées',
      search: 'Rechercher des articles...',
      categories: {
        all: 'Tous les Articles',
        insurance: 'Assurance',
        mortgage: 'Hypothèque',
        investment: 'Investissement'
      },
      noResults: 'Aucun article trouvé',
      readMore: 'Lire la Suite',
      backToBlog: 'Retour au Blogue',
      shareArticle: 'Partager cet article:',
      needAdvice: 'Besoin de Conseils d\'Experts?',
      needAdviceDesc: 'Notre équipe de professionnels licenciés est là pour vous aider à prendre des décisions financières éclairées.',
      speakAdvisor: 'Parler à un Conseiller',
      relatedArticles: 'Articles Connexes'
    },
    // Insurance Page
    insurance: {
      title: 'Solutions d\'Assurance',
      subtitle: 'Protection complète pour vous et vos proches grâce aux principaux assureurs du Canada',
      getQuote: 'Obtenir une Soumission Gratuite',
      sections: {
        lifeHealth: {
          title: 'Protection Vie & Santé',
          subtitle: 'Couverture essentielle pour protéger la sécurité financière de votre famille'
        },
        travel: {
          title: 'Assurance Voyage & Visiteurs',
          subtitle: 'Protection pour les voyageurs et visiteurs au Canada'
        }
      },
      quoteEstimator: {
        title: 'Estimateur de Soumission d\'Assurance',
        subtitle: 'Obtenez une estimation instantanée pour votre couverture d\'assurance',
        disclaimer: 'Ce calculateur fournit des estimations uniquement basées sur les moyennes de l\'industrie. Les primes réelles peuvent varier selon l\'historique de santé et la souscription de l\'assureur. Contactez nos conseillers pour des soumissions précises.',
        calculate: 'Calculer Mon Estimation',
        readyForQuote: 'Prêt pour une Soumission Précise?',
        readyForQuoteDesc: 'Contactez nos conseillers licenciés pour des soumissions personnalisées avec les meilleurs taux de plusieurs assureurs.',
        speakAdvisor: 'Parler à un Conseiller'
      }
    },
    // Team Page
    team: {
      title: 'Rencontrez Notre Équipe',
      subtitle: 'Des professionnels expérimentés dédiés à votre succès financier',
      managingPartners: 'Associés Directeurs',
      mortgageTeam: 'Équipe Hypothécaire',
      insuranceTeam: 'Équipe Assurance & Investissements',
      contactInfo: 'Coordonnées'
    },
    // Testimonials
    testimonials: {
      title: 'Ce Que Disent Nos Clients',
      subtitle: 'Histoires réelles de vrais clients à travers le Canada'
    }
  }
};

export default LanguageProvider;
